/**
 * SWOOP! flash — a transient overlay that pops up briefly whenever the
 * engine emits a `swoop` event. Shows the player name and a short label
 * ("wild" / "four of a kind") so it's obvious what just happened.
 *
 * Multiple back-to-back swoops (same player chains them) replace the
 * previous popup so the latest one is always visible.
 */

import { useEffect, useState } from "react";
import type {
  SwoopPublicState,
} from "../../../../shared/games/swoop.js";
import type { GameEvent } from "../../../../shared/protocol.js";

const FLASH_MS = 1400;

interface FlashState {
  /** Monotonic id so we can re-trigger the same animation if it lands twice. */
  id: number;
  text: string;
  subText: string;
}

export function SwoopFlash({
  events,
  state,
}: {
  events: GameEvent[];
  state: SwoopPublicState;
}) {
  const [flash, setFlash] = useState<FlashState | null>(null);

  useEffect(() => {
    // Look at only the latest swoop event we haven't reflected yet. We
    // key off `events.length` — each new event extends the array.
    if (events.length === 0) return;
    const last = events[events.length - 1]!;
    if (last.kind !== "swoop" || last.type !== "swoop") return;
    const data = (last.data ?? {}) as { seat?: number; reason?: string };
    const seat = typeof data.seat === "number" ? data.seat : -1;
    const name = state.players[seat]?.displayName ?? "Someone";
    const reason =
      data.reason === "wild"
        ? "wild card"
        : data.reason === "four-in-a-row"
        ? "four of a kind"
        : "";
    setFlash({
      id: Date.now(),
      text: `${name} swoops!`,
      subText: reason,
    });
  }, [events.length, events, state.players]);

  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), FLASH_MS);
    return () => clearTimeout(t);
  }, [flash]);

  if (!flash) return null;
  return (
    <div className="swoop-flash" key={flash.id} role="status" aria-live="polite">
      <div className="swoop-flash-headline">SWOOP!</div>
      <div className="swoop-flash-body">{flash.text}</div>
      {flash.subText && <div className="swoop-flash-sub">{flash.subText}</div>}
    </div>
  );
}
